/**
 * MoveProductModal — modal para mover una cantidad de producto entre
 * ubicaciones (bodegas, bodeguitas, propiedades). Extraído de
 * inventario.tsx en round 2026-04-27 (Fase 8) para sacar ~220 LOC del
 * archivo padre y aislar la lógica de "con cargo a quién" + búsqueda
 * de productos similares para consolidación.
 *
 * QUÉ HACE
 *   1. Form con: fecha del movimiento, destino (tipo + id), tipo de
 *      movimiento (packs/unidades), cantidad, "con cargo a" (AF /
 *      Propietario / sin cargo).
 *   2. Búsqueda de productos similares en el destino (la maneja el
 *      padre vía `useEffect` reaccionando a `moveParams.destino_*`) —
 *      acá solo renderizamos la tabla de resultados con rowSelection
 *      radio para elegir el destino-existente con que consolidar.
 *   3. "Con cargo a" se auto-deriva con `deriveCargoA` según los tipos
 *      de origen/destino (ver helper abajo). Cuando la combinación es
 *      inequívoca (bodega → bodega = sin cargo) se BLOQUEA con un
 *      Alert; cuando es ambigua (propiedad → propiedad) se muestra el
 *      Radio.Group para que el admin elija.
 *
 * POR QUÉ ESTÁ AQUÍ Y NO INLINE
 *   - El Modal inline tenía ~220 LOC con dos IIFEs `(() => {...})()`
 *     anidados dentro del JSX (cantidad dinámica + cargo derivado),
 *     muy duros de leer en su contexto original.
 *   - El estado vive en el padre (`moveParams`, `similarProducts`,
 *     `selectedSimilarId`) porque el `useEffect` de buscar similares
 *     y la apertura del modal viven allí. El modal sólo expone los
 *     setters como props para mantener el flujo.
 *   - Mismo comportamiento que el inline; no hay cambio de estado ni
 *     de validación.
 *
 * NO TOCA RED — `onOk` lo provee el padre (handleConfirmMove), que es
 * quien hace `await onMove(moveParams)`. Aislar el modal del transporte
 * facilita testing futuro y reduce el blast radius si cambia el endpoint.
 */

import React from "react";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Table,
} from "antd";
import dayjs from "dayjs";
import { fuzzyIncludes } from "../../../utils/fuzzyMatch";
import type {
  MovimientoParams,
  Producto,
} from "../useInventarioLogic";
import type { Inventory } from "../../../interfaces/api.interfaces";
// Bridge antd estático: los métodos `Modal.x` / `message.x` /
// `notification.x` importados directo de "antd" no funcionan en
// v5 + React 19 (root React huérfano fuera del ConfigProvider).
// Round 2026-05-01 — sweep general post-fix /sueldos.
import { getMsg } from "../../../utils/antdStatic";
import { ahoraChile } from "../../../utils/fechaChile";

const { Option } = Select;

// ─── Helpers puros ──────────────────────────────────────────────────────

/** Tipos de cargo financiero asociado a un movimiento. Strings literales
 *  porque el backend los compara texto-a-texto. Round 2026-07-26: se
 *  eliminó "sin_cargo" (era financieramente idéntico a "AF" — el producto
 *  ya se pagó el día de la compra; el backend normaliza el valor legacy). */
type CargoValue = "AF" | "Propietario" | "";

/** Resultado de `deriveCargoA`. `locked=true` significa que la regla es
 *  inequívoca y el UI no debe permitir cambiarlo. `label` es el mensaje
 *  para el Alert cuando está bloqueado. */
interface CargoDerivation {
  value: CargoValue;
  locked: boolean;
  label?: string;
}

/**
 * Decide a quién se cargan los costos de un movimiento según los tipos
 * de ubicación de ORIGEN y DESTINO.
 *
 * REGLAS DE NEGOCIO (ver docs/modulo-inventario.md y flujo-financiero.md):
 *   - bodega/bodeguita → bodega/bodeguita: cargo "AF" LOCKED y monto $0
 *     (mover stock interno entre bodegas no impacta finanzas — la mercadería
 *     ya estaba comprada; el backend fuerza el monto a 0 por GEOMETRÍA).
 *   - bodega/bodeguita → propiedad: cargo al CLIENTE por default (entrega de
 *     stock a una propiedad = se le cobra al cliente; genera la recuperación
 *     en moveItem, ver G10). El admin puede sobrescribir a "AF" (AF absorbe
 *     el costo — el producto ya se pagó el día de la compra). Para REGALAR
 *     explícitamente: elegir Cliente y editar el cobro a $0 después.
 *   - propiedad → bodega/bodeguita: cargo a AF por default (devolución de
 *     stock que vuelve a inventario AF).
 *   - propiedad → propiedad: ambiguo (puede ser intercambio amigable o
 *     reasignación), no derivamos — el admin elige manualmente.
 *
 * Round 2026-07-26: se eliminó la tercera opción "sin_cargo" (decisión del
 * owner) — era lo mismo que "AF": en ambos casos no se cobra a nadie y el
 * egreso ya quedó registrado en la fecha de la compra.
 *
 * NOTA SOBRE EL VALOR "Propietario": el `value` se mantiene como
 * "Propietario" por contrato histórico (se persiste en
 * `Record.json_data.cargo_a` y `moveItem` lo compara texto-a-texto para
 * disparar la recuperación). Solo el LABEL visible se renombró a "Cliente"
 * (round 2026-06-19, decisión D-E). No migrar el value.
 *
 * NOTA: este helper es puro pero vive aquí (no en helpers externos)
 * porque las reglas son específicas del UI del Move modal. Si en el
 * futuro otros flujos las necesitan, mover a `inventario.helpers.ts`.
 */
function deriveCargoA(
  origenTipo: string,
  destinoTipo: string,
): CargoDerivation {
  const isStorageOrigen =
    origenTipo === "bodegas" || origenTipo === "bodeguitas";
  const isStorageDestino =
    destinoTipo === "bodegas" || destinoTipo === "bodeguitas";

  if (isStorageOrigen && isStorageDestino) {
    return {
      value: "AF",
      locked: true,
      label:
        "Movimiento entre bodegas: sin efecto financiero (el stock ya está pagado; monto $0)",
    };
  }
  if (isStorageOrigen && destinoTipo === "propiedades") {
    return { value: "Propietario", locked: false };
  }
  if (origenTipo === "propiedades" && isStorageDestino) {
    return { value: "AF", locked: false };
  }
  // propiedad → propiedad u otra combinación no contemplada: sin default.
  return { value: "", locked: false };
}

// Re-exportamos para que el padre pueda usarlo en otros call sites
// (ej. al abrir el modal con default de cargo precomputado). Mantiene
// el contrato vigente sin obligar al padre a importar dos sitios.
export { deriveCargoA };

// ─── Tipos de props ─────────────────────────────────────────────────────

interface MoveProductModalProps {
  /** Visibilidad (controlada por padre). */
  open: boolean;
  /** Cierre/cancel — padre setea `setIsMoveModalVisible(false)` y
   *  opcionalmente limpia state derivado. */
  onCancel: () => void;
  /** Confirmación — padre llama a `onMove(moveParams)` y muestra toast.
   *  Lo dejamos async para futura integración con loaders. */
  onConfirm: () => void | Promise<void>;
  /** Estado de los parámetros del movimiento — owner del padre porque
   *  el `useEffect` de buscar similares depende de él. */
  moveParams: MovimientoParams;
  setMoveParams: React.Dispatch<React.SetStateAction<MovimientoParams>>;
  /** Resultados de la búsqueda de similares — populado por el padre. */
  similarProducts: Inventory[];
  /** Id del similar elegido para consolidar (radio selection). */
  selectedSimilarId: number | null;
  setSelectedSimilarId: React.Dispatch<React.SetStateAction<number | null>>;
  /** Catálogos para los Selects de destino. Pasamos los 3 tipos juntos
   *  para que el componente decida cuál mostrar según `destino_tipo`. */
  bodegas: Array<{ id: number | string; nombre: string }>;
  bodeguitas: Array<{ id: number | string; nombre: string }>;
  properties: Array<{ id: number | string; nombre: string }>;
  /** Inventario actual (para conocer máximos de cantidad disponibles
   *  según el producto que se está moviendo). */
  inventario: Producto[];
}

// ─── Componente ─────────────────────────────────────────────────────────

const MoveProductModal: React.FC<MoveProductModalProps> = ({
  open,
  onCancel,
  onConfirm,
  moveParams,
  setMoveParams,
  similarProducts,
  selectedSimilarId,
  setSelectedSimilarId,
  bodegas,
  bodeguitas,
  properties,
  inventario,
}) => {
  return (
    <Modal
      open={open}
      title="Mover producto"
      onCancel={onCancel}
      onOk={onConfirm}
      destroyOnClose
    >
      <Form layout="vertical">
        {/*
         * Fecha — default hoy. El backend la usa para registrar el
         * InventoryTransaction; importante para reportes de movimientos
         * por mes en `/historial`.
         */}
        <Form.Item label="Fecha del movimiento">
          <DatePicker
            value={moveParams.fecha ? dayjs(moveParams.fecha) : ahoraChile()}
            onChange={(d: any) =>
              setMoveParams((p) => ({
                ...p,
                fecha: d
                  ? d.format("YYYY-MM-DD")
                  : ahoraChile().format("YYYY-MM-DD"),
              }))
            }
            format="DD/MM/YYYY"
            style={{ width: "100%" }}
          />
        </Form.Item>

        {/*
         * Destino tipo — al cambiar:
         *   1. resetea destino_id (los Selects de cada tipo tienen
         *      universos distintos).
         *   2. recalcula `cargo_a` con `deriveCargoA` (la regla de
         *      negocio depende de la combinación origen/destino).
         */}
        <Form.Item label="Destino Tipo" required>
          <Select
            value={moveParams.destino_tipo}
            onChange={(value) => {
              const { value: cargoValue } = deriveCargoA(
                moveParams.origen_tipo,
                value,
              );
              setMoveParams((p) => ({
                ...p,
                destino_tipo: value,
                destino_id: undefined,
                cargo_a: cargoValue,
              }));
            }}
          >
            <Option value="bodegas">Bodega</Option>
            <Option value="bodeguitas">Bodeguita</Option>
            <Option value="propiedades">Propiedad</Option>
          </Select>
        </Form.Item>

        {moveParams.destino_tipo && (
          <Form.Item label="Destino" required>
            <Select
              showSearch
              filterOption={(input, option) =>
                fuzzyIncludes(option?.children, input)
              }
              placeholder="Seleccionar destino"
              value={moveParams.destino_id}
              onChange={(value) =>
                setMoveParams((p) => ({ ...p, destino_id: value }))
              }
            >
              {(moveParams.destino_tipo === "bodegas"
                ? bodegas
                : moveParams.destino_tipo === "bodeguitas"
                  ? bodeguitas
                  : properties
              ).map((o) => (
                <Option key={o.id} value={o.id}>
                  {o.nombre}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {/*
         * Tipo de movimiento (packs vs unidades). El producto puede ser
         * "pack" (caja con N unidades) o "unidades sueltas" — al
         * mover packs, los descuenta como packs; al mover unidades,
         * descuenta de los sueltos.
         */}
        <Form.Item label="Tipo de movimiento">
          <Radio.Group
            value={moveParams.es_pack}
            onChange={(e) => {
              // Round 2026-08-06 — al cambiar de modo se limpia la cantidad del
              // OTRO modo. Antes solo se escribía `es_pack` y el valor viejo
              // viajaba igual al backend: el modal precarga `cantidad_de_packs:
              // 1` para productos pack, así que mover unidades mandaba "5
              // unidades Y 1 pack" y la transacción se valorizaba como un pack
              // entero (bug de plata; el backend ahora también lo blinda en
              // `cantidadesDeMovimiento`, pero mandar basura no es correcto ni
              // aunque el servidor la ignore).
              const esPack = e.target.value;
              setMoveParams((p) => ({
                ...p,
                es_pack: esPack,
                cantidad_de_packs: esPack ? p.cantidad_de_packs : 0,
                cantidad_de_unidades: esPack ? 0 : p.cantidad_de_unidades,
              }));
            }}
          >
            <Radio value={true}>Packs</Radio>
            <Radio value={false}>Unidades</Radio>
          </Radio.Group>
        </Form.Item>

        {/*
         * Cantidad (con botón "Mover todo (N)"). IIFE porque depende de
         * cuál es el producto actual del moveParams.productId — el
         * producto trae `cantidadDePacks` y `cantidadDeUnidades` y el
         * máximo cambia según `es_pack`.
         */}
        {(() => {
          const prod = inventario.find(
            (i) => i.id === moveParams.productId,
          );
          const maxPacks = prod?.cantidadDePacks || 0;
          const maxUnidades = prod?.cantidadDeUnidades || 0;
          const max = moveParams.es_pack ? maxPacks : maxUnidades;
          // Field name dinámico — el contrato del state usa nombres
          // distintos para packs vs unidades. Mantenemos esa separación
          // porque el backend también la diferencia (algunos productos
          // existen solo como packs, otros solo unidades, otros ambos).
          const field = moveParams.es_pack
            ? "cantidad_de_packs"
            : "cantidad_de_unidades";
          const value = moveParams.es_pack
            ? moveParams.cantidad_de_packs
            : moveParams.cantidad_de_unidades;
          return (
            <Form.Item
              label={
                moveParams.es_pack
                  ? "Cantidad de packs"
                  : "Cantidad de unidades"
              }
              required
            >
              {/* Visualmente "agrupados" — InputNumber + Button con
                  bordes pegados a izq/der respectivamente para que se
                  vean como un solo control. */}
              <div
                style={{ display: "flex", alignItems: "stretch", gap: 0 }}
              >
                <InputNumber
                  min={1}
                  max={max}
                  value={value || undefined}
                  onChange={(v) =>
                    setMoveParams((p) => ({ ...p, [field]: v ?? 0 }))
                  }
                  style={{
                    width: 100,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                />
                <Button
                  onClick={() =>
                    setMoveParams((p) => ({ ...p, [field]: max }))
                  }
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                >
                  Mover todo ({max})
                </Button>
              </div>
            </Form.Item>
          );
        })()}

        {/*
         * "Con cargo a" — auto-derivado por origen/destino. Si la regla
         * es inequívoca (locked=true), mostramos Alert; si es ambigua,
         * el Radio.Group permite elegir manualmente.
         */}
        {moveParams.destino_tipo &&
          (() => {
            const cargoAuto = deriveCargoA(
              moveParams.origen_tipo,
              moveParams.destino_tipo,
            );
            if (cargoAuto.locked) {
              return (
                <Form.Item label="Con cargo a:">
                  <Alert
                    message={cargoAuto.label}
                    type="info"
                    showIcon
                    style={{ marginBottom: 0 }}
                  />
                </Form.Item>
              );
            }
            return (
              <Form.Item label="Con cargo a:" required>
                <Radio.Group
                  value={moveParams.cargo_a}
                  onChange={(e) =>
                    setMoveParams((p) => ({
                      ...p,
                      cargo_a: e.target.value,
                    }))
                  }
                >
                  {/* Solo 2 opciones (round 2026-07-26): "AF absorbe" cubre
                      también el viejo "Sin cargo" (idénticos en caja: nadie
                      recibe un cobro; el egreso ya quedó en la compra). Para
                      regalar explícitamente: Cliente + editar el cobro a $0. */}
                  <Radio value={"AF"}>Aloja Fácil (absorbe el costo)</Radio>
                  {/* Label "Cliente" (D-E); el value se mantiene "Propietario"
                      por contrato backend (moveItem lo compara para la recuperación). */}
                  <Radio value={"Propietario"}>Cliente (se le cobra)</Radio>
                </Radio.Group>
              </Form.Item>
            );
          })()}

        {/*
         * Productos similares en el destino — si los hay, los mostramos
         * en una tablita para que el admin pueda CONSOLIDAR (sumar al
         * existente en lugar de crear un registro nuevo). Si no hay,
         * mostramos un Alert info.
         *
         * Nota sobre packs: el backend siempre crea registro nuevo si
         * `unidadesPorPack` difiere — un pack de 6 y un pack de 12 son
         * cosas distintas para reportes financieros aunque el "nombre"
         * sea igual. Documentado en la nota inline al final.
         */}
        {similarProducts.length > 0 ? (
          <>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              Objetos similares en el destino
            </div>
            <Table
              size="small"
              rowKey="id"
              dataSource={similarProducts}
              pagination={false}
              scroll={{ x: "max-content" }}
              rowSelection={{
                type: "radio",
                selectedRowKeys: selectedSimilarId
                  ? [selectedSimilarId]
                  : [],
                onChange: (keys) => {
                  const id = (keys[0] as number) ?? null;
                  setSelectedSimilarId(id);
                  setMoveParams((p) => ({
                    ...p,
                    destinoProductId: id ?? undefined,
                  }));
                },
              }}
              columns={[
                { title: "Nombre", dataIndex: "nombre" },
                {
                  title: "Tipo",
                  render: (_, r) => (r.esPack ? "Pack" : "Unidades"),
                },
                {
                  title: "U/pack",
                  dataIndex: "unidadesPorPack",
                  render: (v) => v ?? "—",
                },
                {
                  title: "Packs",
                  dataIndex: "cantidadDePacks",
                  render: (v) => v ?? "—",
                },
                {
                  title: "Unidades",
                  dataIndex: "cantidadDeUnidades",
                  render: (v) => v ?? "—",
                },
              ]}
            />
            <Space style={{ marginTop: 8 }}>
              <Button
                onClick={() => {
                  setSelectedSimilarId(null);
                  setMoveParams((p) => ({
                    ...p,
                    destinoProductId: undefined,
                  }));
                }}
              >
                No consolidar (crear nuevo)
              </Button>
              <Button
                type="primary"
                disabled={!selectedSimilarId}
                onClick={() =>
                  getMsg()?.success("Se consolidará con el seleccionado")
                }
              >
                Usar seleccionado
              </Button>
            </Space>
            <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
              Nota: si el seleccionado es <b>pack</b> con distinto{" "}
              <i>unidadesPorPack</i>, el backend creará un ítem nuevo (no
              consolidará sobre ese registro). Si es <b>unidades</b> y
              mueves unidades, se sumará directo.
            </div>
          </>
        ) : (
          <Alert
            type="info"
            showIcon
            message="No se encontraron objetos similares para consolidar"
          />
        )}
      </Form>
    </Modal>
  );
};

export default MoveProductModal;
